import axios from 'axios';
import { getAuthToken, saveAuthToken, getRefreshToken, saveRefreshToken, removeAuthToken, removeRefreshToken } from '@/utils/auth';
import store from '@/store';
import { isTokenExpired } from '@/utils/auth';

// --- Configuration --- 
// TODO: Replace with your actual backend API base URL
// It's recommended to use environment variables for this
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api'; 

// --- Axios Instance --- 
const apiService = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 second timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
// Queue of requests that are waiting for the token to be refreshed
let refreshSubscribers = [];

// Function to handle token refresh
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject('No refresh token available');
  }
  
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
      refresh: refreshToken
    });
    
    const { access, refresh } = response.data;
    saveAuthToken(access);
    // Update refresh token if a new one was provided
    if (refresh) {
      saveRefreshToken(refresh);
    }
    
    return access;
  } catch (error) {
    // If refresh token fails, log out user
    removeAuthToken();
    removeRefreshToken();
    return Promise.reject(error);
  }
};

// Function to add failed requests to the queue
const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

// Function to retry failed requests after token refresh
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// --- Interceptors --- 

// Request Interceptor: Add Auth Token if available
apiService.interceptors.request.use(
  async (config) => {
    const token = getAuthToken();
    
    // Avoid attempting to refresh while we are already **calling** the refresh endpoint
    // (otherwise the interceptor will recursively call `refreshToken` forever)
    const isRefreshRequest = config.url && config.url.includes('/auth/token/refresh');
    
    // If token exists and is expired, try to refresh it
    if (token && isTokenExpired() && !isRefreshRequest) {
      try {
        await store.dispatch('refreshToken');
        // Update token after refresh
        const newToken = getAuthToken();
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`;
        }
      } catch (error) {
        // If refresh fails, continue without auth (will likely fail)
        console.error('Token refresh failed in interceptor:', error);
      }
    } else if (token) {
      // Token exists and is valid, use it
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    // Handle request error
    console.error('Axios request error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle global errors (e.g., 401 Unauthorized)
apiService.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx cause this function to trigger
    return response;
  },
  async (error) => {
    // Any status codes outside the range of 2xx cause this function to trigger
    console.error('Axios response error:', error.response || error.message);
    
    // Handle 401 Unauthorized (token expired)
    if (error.response && error.response.status === 401) {
      // -----------------------------------------------
      // Special case: remain on the registration wizard
      // -----------------------------------------------
      const originalRequest = error.config;
      const onRegistrationRoute = window.location.pathname.startsWith('/register');
      const registrationEndpoint = originalRequest.url && originalRequest.url.startsWith('/business/');

      if (onRegistrationRoute || registrationEndpoint) {
        console.warn('401 received during registration flow – skipping auto-redirect to login');
        return Promise.reject(error);
      }
      
      // Prevent infinite refresh loops
      if (originalRequest._retry) {
        // If we already tried to refresh once, logout and redirect
        removeAuthToken();
        removeRefreshToken();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      // Mark that we're going to retry this request
      originalRequest._retry = true;
      
      // Only try to refresh the token once at a time
      if (!isRefreshing) {
        isRefreshing = true;
        
        try {
          // Try to refresh the token
          const newToken = await refreshAccessToken();
          
          // If successful, notify all waiting requests
          isRefreshing = false;
          onTokenRefreshed(newToken);
          
          // Update the failed request and retry it
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return apiService(originalRequest);
        } catch (refreshError) {
          // If refresh fails, redirect to login
          isRefreshing = false;
          console.error('Token refresh failed:', refreshError);
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // If refresh is already in progress, add request to queue
        return new Promise((resolve) => {
          subscribeTokenRefresh(newToken => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(apiService(originalRequest));
          });
        });
      }
    }
    
    // TODO: Add more specific error handling based on status codes or error structure

    return Promise.reject(error);
  }
);

// --- Exported API Methods --- 

export default {
  get(url, config = {}) {
    return apiService.get(url, config);
  },

  post(url, data = {}, config = {}) {
    return apiService.post(url, data, config);
  },

  // Add generic HTTP wrapper methods for convenience
  put(url, data = {}, config = {}) {
    return apiService.put(url, data, config);
  },

  patch(url, data = {}, config = {}) {
    return apiService.patch(url, data, config);
  },

  delete(url, config = {}) {
    return apiService.delete(url, config);
  },

  // --- Registration Flow --- 
  registerStep1(data) {
    // data: { email, last_name, phone_number, birthdate, hcaptcha_response }
    return apiService.post('/business/register/', data);
  },
  
  verifyEmail(data) {
    // data: { email, code }
    return apiService.post('/business/verify-email/', data);
  },
  
  setPassword(data) {
    // data: { email, password, confirm_password }
    return apiService.post('/business/set-password/', data);
  },
  
  // --- Expert Profile --- 
  getExpertProfile() {
    // This requires authentication (token must be set)
    return apiService.get('/experts/profile/me/');
  },
  
  updateExpertProfile(data) {
    // data: { full_name, profile_picture, ..., background }
    // This requires authentication (token must be set)
    // Use PUT for full update, PATCH for partial
    return apiService.put('/experts/profile/me/', data);
  },
  
  // --- Authentication (Example) --- 
  login(credentials) {
    // credentials: { username, password } or { email, password }
    return apiService.post('/auth/token/', credentials);
  },

  // Refresh the JWT access token
  refreshToken(refreshToken) {
    return apiService.post('/auth/token/refresh/', { refresh: refreshToken });
  },

  // Verify if token is still valid
  verifyToken(token) {
    return apiService.post('/auth/token/verify/', { token });
  },

  // Logout - blacklist refresh token
  logout() {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      return apiService.post('/auth/token/blacklist/', { refresh: refreshToken })
        .finally(() => {
          removeAuthToken();
          removeRefreshToken();
        });
    } else {
      removeAuthToken();
      removeRefreshToken();
      return Promise.resolve();
    }
  },

  // --- VIS Methods ---
  
  // Get list of all Visual Identity Systems
  getVisualIdentitySystems(params = {}) {
    return apiService.get('/brands/vis/', { params });
  },
  
  // Get a single VIS by ID
  getVisualIdentitySystem(id) {
    return apiService.get(`/brands/vis/${id}/`);
  },
  
  // Create a new VIS
  createVisualIdentitySystem(data) {
    return apiService.post('/brands/vis/', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Update a VIS
  updateVisualIdentitySystem(id, data) {
    return apiService.put(`/brands/vis/${id}/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Delete a VIS
  deleteVisualIdentitySystem(id) {
    return apiService.delete(`/brands/vis/${id}/`);
  },
  
  // Get materials for a VIS
  getVisMaterials(visId) {
    return apiService.get('/brands/vis-materials/', {
      params: { vis_id: visId }
    });
  },
  
  // Get list of all brands
  getBrands() {
    return apiService.get('/brands/brands/');
  },
  
  // Get list of geographical regions
  getGeographicalRegions() {
    return apiService.get('/brands/geographical-regions/');
  },
  
  // --- Assessment Methods ---
  
  // Retrieve a paginated list of assessments
  getAssessments(params = {}) {
    // GET /assessments/?<query params>
    return apiService.get('/assessments/', { params });
  },
  
  // Retrieve a single assessment (wrapper kept for completeness – store accesses apiService.get directly for detail)
  getAssessment(id) {
    return apiService.get(`/assessments/${id}/`);
  },

  // Create a new assessment
  createAssessment(data) {
    return apiService.post('/assessments/', data);
  },

  // Update an existing assessment (partial update by default)
  updateAssessment(id, data) {
    return apiService.patch(`/assessments/${id}/`, data);
  },

  // Delete an assessment
  deleteAssessment(id) {
    return apiService.delete(`/assessments/${id}/`);
  },

  // --- Question Methods ---

  // Create a new question for an assessment
  // If you pass FormData (e.g. including an image file) Axios will automatically
  // set the correct multipart headers, otherwise we fallback to JSON.
  createQuestion(data) {
    const isFormData = (typeof FormData !== 'undefined') && data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return apiService.post('/assessments/questions/', data, config);
  },

  // Link an existing VIS material to a question
  createQuestionVisMaterial(data) {
    return apiService.post('/assessments/question-vis-materials/', data);
  },

  // --- Assessment Response Methods ---

  // Retrieve assessment responses (optionally filtered via query params)
  getAssessmentResponses(params = {}) {
    return apiService.get('/assessments/assessment-responses/', { params });
  },

  // Create a new assessment response
  createAssessmentResponse(data) {
    return apiService.post('/assessments/assessment-responses/', data);
  },

  // Mark an assessment response as complete
  completeAssessmentResponse(responseId) {
    return apiService.post(`/assessments/assessment-responses/${responseId}/mark_complete/`);
  },

  // --- Question Response Methods ---

  // Create or update a question response (the DRF endpoint upserts on POST)
  saveQuestionResponse(data) {
    return apiService.post('/assessments/question-responses/', data);
  },

  // Get question responses with optional filtering
  getQuestionResponses(params = {}) {
    return apiService.get('/assessments/question-responses/', { params });
  },

  // Template Methods
  getAssessmentTemplates(params = {}) {
    return apiService.get('/assessments/templates/', { params });
  },

  getAssessmentTemplate(id) {
    return apiService.get(`/assessments/templates/${id}/`);
  },

  createAssessmentTemplate(data) {
    return apiService.post('/assessments/templates/', data);
  },

  updateAssessmentTemplate(id, data) {
    return apiService.put(`/assessments/templates/${id}/`, data);
  },

  deleteAssessmentTemplate(id) {
    return apiService.delete(`/assessments/templates/${id}/`);
  },

  getTemplateCategories() {
    return apiService.get('/assessments/templates/categories/');
  },

  getAssessmentSummaryStats(id) {
    return this.get(`/assessments/${id}/summary-stats/`);
  },

  getAssessmentExpertProgress(id) {
    return this.get(`/assessments/${id}/expert-progress/`);
  },

  // --- Summary Methods ---
  
  // Get summaries for an assessment
  getSummaries(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.get(`/assessments/summaries/${queryString ? '?' + queryString : ''}`);
  },

  // Get a specific summary
  getSummary(id) {
    return this.get(`/assessments/summaries/${id}/`);
  },

  // Generate a new summary for a question
  generateSummary(assessmentId, questionId) {
    return this.post('/assessments/summaries/generate/', {
      assessment_id: assessmentId,
      question_id: questionId
    });
  },

  // Refresh an existing summary
  refreshSummary(summaryId) {
    return this.post(`/assessments/summaries/${summaryId}/refresh/`);
  },

  // Update a summary
  updateSummary(id, data) {
    return this.patch(`/assessments/summaries/${id}/`, data);
  },

  // Delete a summary
  deleteSummary(id) {
    return this.delete(`/assessments/summaries/${id}/`);
  },

  // --- PDF Export methods ---
  exportAssessmentToPDF(assessmentId) {
    return this.get(`/assessments/${assessmentId}/export-pdf/`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  exportAssessmentToCSV(assessmentId) {
    return this.get(`/assessments/${assessmentId}/export-csv/`, {
      responseType: 'blob'
    });
  },

  // Form options endpoint
  async getFormOptions() {
    return this.get('/experts/form-options/');
  }
}; 